import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, Loading } from "../components/Layout";
import { toast } from "sonner";
import { Save } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function RegionCard({ r, onSave }) {
  const [form, setForm] = useState(r);
  const toggle = (d) => setForm((f) => ({ ...f, business_days: f.business_days.includes(d) ? f.business_days.filter((x) => x !== d) : [...f.business_days, d] }));
  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid={`region-card-${r.name}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-sm font-semibold uppercase tracking-wider text-[#1C2530]">{r.name}</div>
        <span className="font-mono text-xs text-[#1C2530]/50">{form.timezone}</span>
      </div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">Business Days</div>
      <div className="flex gap-1 mb-4">
        {DAYS.map((d) => (
          <button key={d} onClick={() => toggle(d)}
            className={`w-10 py-1.5 font-mono text-[10px] uppercase border transition-colors ${form.business_days.includes(d) ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/50 hover:bg-[#EFECE5]"}`}>
            {d}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Start Hour</label>
          <input type="number" min="0" max="23" value={form.start_hour} onChange={(e) => setForm({ ...form, start_hour: +e.target.value })}
            className="w-full mt-1 border border-[#DFDAD0] px-2 py-1 text-sm font-mono outline-none focus:border-[#1C2530]" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">End Hour</label>
          <input type="number" min="0" max="23" value={form.end_hour} onChange={(e) => setForm({ ...form, end_hour: +e.target.value })}
            className="w-full mt-1 border border-[#DFDAD0] px-2 py-1 text-sm font-mono outline-none focus:border-[#1C2530]" />
        </div>
      </div>
      <div className="font-mono text-[10px] text-[#1C2530]/50 mb-4">Holidays: {form.holidays.join(", ") || "none"}</div>
      <div className="border-t border-[#DFDAD0] pt-3 mb-4">
        <label className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Pre-meeting reminder lead time (hours)</label>
        <input type="number" min="1" max="168" value={form.reminder_lead_hours ?? 24} onChange={(e) => setForm({ ...form, reminder_lead_hours: +e.target.value })}
          data-testid={`reminder-lead-${r.name}`}
          className="w-full mt-1 border border-[#DFDAD0] px-2 py-1 text-sm font-mono outline-none focus:border-[#1C2530]" />
        <div className="font-mono text-[9px] text-[#1C2530]/40 mt-1">First reminder auto-sends this many hours before the meeting.</div>
      </div>
      <button onClick={() => onSave(form)} data-testid={`save-region-${r.name}`}
        className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644]">
        <Save className="w-3.5 h-3.5" /> Save Config
      </button>
    </div>
  );
}

export default function Regions() {
  const [regions, setRegions] = useState(null);
  const load = () => api.get("/regions").then((r) => setRegions(r.data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try { await api.post("/regions", form); toast.success(`${form.name} config saved`); load(); }
    catch { toast.error("Save failed"); }
  };

  return (
    <>
      <PageHeader eyebrow="ORCHESTRATION · SENDS QUEUE OUTSIDE THESE WINDOWS" title="Regional Scheduling Config" hint="Working hours and timezone per region. Any automated send outside these windows waits in the queue until the region opens." />
      <div className="p-8">
        {!regions ? <Loading /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {regions.map((r) => <RegionCard key={r.id} r={r} onSave={save} />)}
          </div>
        )}
      </div>
    </>
  );
}
